import { ApiError, ResourceClient, type HttpClient } from "@dl/web-core-js/api";
import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import {
  columns as defineColumns,
  DataTable,
  type RowEditorConfig,
} from "@dl/web-core-js/table";
import { useQueries, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type ColumnFactory<T> = ReturnType<typeof defineColumns<T>>;

/** A row of another reference table, as a lookup hands it to columns and fields. */
export type LookupRow = { id: number } & Record<string, unknown>;
/** Lookup name → its rows; an empty list until they have loaded. */
export type Lookups = Record<string, LookupRow[]>;

export interface ConfigPageSpec<T extends { id: number }> {
  /** REST base path, e.g. `/product/designer`. */
  path: string;
  /** Query cache key segment. */
  cacheKey: string;
  titleKey: string;
  titleFallback: string;
  /** View code gating the page; omit for role-only screens. */
  /**
   * Required, so a config page cannot ship ungated by omission. Seven of these
   * once did. Use PUBLIC_VIEW for one that genuinely needs no permission.
   */
  view: string;
  columns: (
    c: ColumnFactory<T>,
    t: (key: string, fallback: string) => string,
    lookups: Lookups,
  ) => ReturnType<ColumnFactory<T>["text"]>[];
  /**
   * Other reference tables the grid names or picks from: an operation's
   * department, a station's site. Loaded beside the rows under the same
   * `["config", cacheKey]` key their own config page uses, so editing one there
   * shows here at once.
   */
  lookups?: Record<string, { path: string; cacheKey: string }>;
  /**
   * Other query keys holding these rows, refreshed after every save, delete or
   * reorder. The part screens cache units and groups under their own keys;
   * without this they keep showing the old values after an edit here.
   */
  alsoInvalidates?: QueryKey[];
  /**
   * Inline editor spec. Omit for read-only screens. `newRow` presence controls
   * whether adding is offered; `canDelete` whether rows can be removed — the
   * fixed-row screens (currency, global rules) edit but never add or delete.
   */
  editor?: {
    /** A function when a field picks from a lookup. */
    fields: RowEditorConfig<T>["fields"] | ((lookups: Lookups) => RowEditorConfig<T>["fields"]);
    newRow?: () => T;
    validate?: RowEditorConfig<T>["validate"];
    canDelete?: boolean;
    /** What deleting does beyond removing the row, said in the confirmation. */
    deleteMessage?: (t: (key: string, fallback: string) => string) => string;
    /**
     * Fields no two rows may share, e.g. `code`. Checked against the loaded
     * rows before saving, so a duplicate is named on the field instead of
     * coming back from the server as an unexplained failure.
     */
    unique?: Array<keyof T & string>;
  };
  /**
   * The rows have an order the business cares about. The grid shows them in
   * that order, with drag handles; dropping a row renumbers the field on every
   * row that moved and saves them — nobody types "30" into a box to put a row
   * third. Sorting a column hides the handles, since a drag against a sort
   * would write an order the screen immediately contradicts.
   */
  order?: {
    get: (row: T) => number;
    set: (row: T, order: number) => T;
  };
}

/**
 * The config screens are one shape: a reference table with inline editing.
 * Thirteen hand-written pages would be thirteen chances to wire a cache key or
 * a delete confirmation differently; a spec keeps each screen to its columns,
 * fields and endpoint, which is also exactly how they differ in Vaadin.
 */
export function makeConfigPage<T extends { id: number }>(spec: ConfigPageSpec<T>) {
  const Grid = makeConfigGrid(spec);
  return function ConfigPage() {
    const { t } = useTranslation();
    const { http, hasView } = useAuth();

    const client = useMemo(() => new ResourceClient<T>(http as HttpClient, spec.path), [http]);
    const allowed = spec.view ? hasView(spec.view) : true;

    // Only the failure matters here; the grid owns the rows. Same query key,
    // so React Query serves both from one fetch.
    const { error } = useQuery({
      queryKey: ["config", spec.cacheKey],
      queryFn: () => client.findAll(),
      enabled: allowed,
    });

    if (!allowed) {
      return (
        <>
          <PageHeader title={t(spec.titleKey, spec.titleFallback)} />
          <div className="p-4 lg:p-6">
            <p className="text-[13px] text-ink-muted">
              {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
            </p>
          </div>
        </>
      );
    }

    if (error) {
      return (
        <>
          <PageHeader title={t(spec.titleKey, spec.titleFallback)} />
          <div className="p-4 lg:p-6">
            <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
              {t("error.generic", "Something went wrong. Please try again.")}
            </p>
          </div>
        </>
      );
    }

    return (
      <>
        <PageHeader title={t(spec.titleKey, spec.titleFallback)} />
        <Grid />
      </>
    );
  };
}

/**
 * The same table without the page around it.
 *
 * A settings screen that gathers several small tables needs the grid but not
 * its header, its access guard or its full-bleed height: those belong to the
 * page, and there is now one page holding several of these.
 */
export function makeConfigGrid<T extends { id: number }>(spec: ConfigPageSpec<T>) {
  const lookupEntries = Object.entries(spec.lookups ?? {});
  return function ConfigGrid() {
    const { t } = useTranslation();
    const { http } = useAuth();
    const queryClient = useQueryClient();

    const client = useMemo(() => new ResourceClient<T>(http as HttpClient, spec.path), [http]);

    const { data, isLoading, isError } = useQuery({
      queryKey: ["config", spec.cacheKey],
      queryFn: () => client.findAll(),
    });

    const lookupResults = useQueries({
      queries: lookupEntries.map(([, lookup]) => ({
        queryKey: ["config", lookup.cacheKey],
        queryFn: () => new ResourceClient<LookupRow>(http as HttpClient, lookup.path).findAll(),
      })),
    });
    const lookupData = lookupResults.map((result) => result.data);
    const lookups = useMemo<Lookups>(
      () => Object.fromEntries(lookupEntries.map(([name], index) => [name, lookupData[index] ?? []])),
      // The data references only change when a lookup refetches.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      lookupData,
    );

    const refresh = async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["config", spec.cacheKey] }),
        ...(spec.alsoInvalidates ?? []).map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      ]);
    };
    const cols = useMemo(() => spec.columns(defineColumns<T>(), t, lookups), [t, lookups]);

    // In the business order when there is one; the server already sends it so, this only guards a stale cache.
    const rows = useMemo(() => {
      const list = data ?? [];
      const order = spec.order;
      return order ? [...list].sort((a, b) => order.get(a) - order.get(b)) : list;
    }, [data]);

    // The latest rows, for the duplicate check, which runs inside the editor's callbacks.
    const rowsRef = useRef(rows);
    rowsRef.current = rows;

    const reordering = useRef(false);
    const reorder = async (visible: T[]) => {
      const order = spec.order;
      if (!order || reordering.current) return;
      // The grid hands over the rows it shows, which is a subset when a filter
      // is on. Numbering only those would collide with the hidden rows'
      // positions, so the moved rows go back into the slots they held in the
      // full list and the whole list is numbered.
      const moved = new Set(visible.map((row) => row.id));
      const queue = [...visible];
      const next = rows.map((row) => (moved.has(row.id) ? (queue.shift() ?? row) : row));
      // Renumber in tens so a later insert by hand still has room; save only what moved.
      const changed = next
        .map((row, index) => ({ row, wanted: (index + 1) * 10 }))
        .filter(({ row, wanted }) => order.get(row) !== wanted)
        .map(({ row, wanted }) => order.set(row, wanted));
      if (changed.length === 0) return;
      reordering.current = true;
      const progress = toast.loading(t("config.order.saving", "Saving the order"));
      try {
        for (const row of changed) await client.update(row);
        toast.success(t("config.order.saved", "Order saved"), { id: progress });
      } catch (error) {
        toast.error(failureMessage(error, t("error.generic", "Something went wrong. Please try again."), t), { id: progress });
      } finally {
        reordering.current = false;
        await refresh();
      }
    };

    // Enter pressed twice, or Enter and a click, would otherwise post the same new row twice.
    const pendingSave = useRef<Promise<void> | null>(null);

    const editor = useMemo<RowEditorConfig<T> | undefined>(() => {
      if (!spec.editor) return undefined;
      const { fields, newRow, validate, canDelete, deleteMessage, unique } = spec.editor;
      const labelOf = (id: string) => {
        const column = cols.find((candidate) => candidate.id === id);
        if (!column) return id;
        if (typeof column.header === "string") return column.header;
        const key = (column.meta as { headerKey?: string } | undefined)?.headerKey;
        return key ? t(key, id) : id;
      };
      const checkUnique = (row: T): Record<string, string> => {
        const errors: Record<string, string> = {};
        for (const key of unique ?? []) {
          const value = String(row[key] ?? "").trim();
          if (value && rowsRef.current.some((other) => other.id !== row.id && String(other[key] ?? "").trim() === value))
            errors[key] = t("config.error.duplicate", "Already used by another row.");
        }
        return errors;
      };
      return {
        fields: typeof fields === "function" ? fields(lookups) : fields,
        ...(newRow ? { newRow } : {}),
        ...(validate || unique
          ? {
              validate: (row: T, isNew: boolean) => {
                const errors = { ...checkUnique(row), ...(validate?.(row, isNew) ?? {}) };
                const first = Object.entries(errors)[0];
                if (!first) return null;
                // The cell only outlines itself and keeps the reason in a tooltip,
                // so say which field and why where it is seen.
                toast.error(`${labelOf(first[0])}: ${first[1]}`, { id: `config-invalid-${spec.cacheKey}` });
                return errors;
              },
            }
          : {}),
        onSave: (row, isNew) => {
          if (pendingSave.current) return pendingSave.current;
          const run = (async () => {
            try {
              const clean = trimStrings(row);
              if (isNew) await client.create(clean);
              else await client.update(clean);
            } catch (error) {
              throw new Error(
                failureMessage(
                  error,
                  t("config.error.save", "Could not save. Another row may already use this code or name."),
                  t,
                ),
              );
            }
            await refresh();
            toast.success(t("notification.saved", "Saved"));
          })();
          pendingSave.current = run;
          return run.finally(() => {
            pendingSave.current = null;
          });
        },
        ...(canDelete
          ? {
              onDelete: async (row: T) => {
                try {
                  await client.remove(row.id);
                } catch (error) {
                  throw new Error(
                    failureMessage(
                      error,
                      t("config.error.delete", "Could not delete: it is probably still in use."),
                      t,
                    ),
                  );
                }
                await refresh();
                toast.success(t("notification.deleted", "Deleted"));
              },
              deleteConfirm: () => ({
                title: t("config.delete.title", "Delete this entry?"),
                ...(deleteMessage ? { message: deleteMessage(t) } : {}),
              }),
            }
          : {}),
      };
    }, [client, t, queryClient, lookups, cols]);

    return (
      <DataTable
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={isLoading}
        {...(editor ? { editor } : {})}
        {...(spec.order ? { onReorder: (next: T[]) => void reorder(next) } : {})}
        emptyMessage={
          isError
            ? t("error.generic", "Something went wrong. Please try again.")
            : t("table.empty", "Nothing to show")
        }
      />
    );
  };
}

/**
 * What to tell a person when the server refused. A refusal with a sentence of
 * its own (the 409s) says it; a bare status code ("500 /unit") says nothing a
 * person can act on, so the caller's sentence replaces it.
 */
function failureMessage(error: unknown, fallback: string, t: (key: string, fallback: string) => string): string {
  if (error instanceof ApiError) {
    const body = error.body;
    if (body && typeof body === "object" && "message" in body && typeof body.message === "string" && body.message)
      return body.message;
    if (error.isForbidden) return t("config.error.forbidden", "Your account cannot change this.");
    // A database refusal the server has no sentence for comes back as a 500.
    if (error.status === 409 || error.status >= 500) return fallback;
  }
  return t("error.generic", "Something went wrong. Please try again.");
}

/** Leading and trailing blanks are typos: " box" and "box" would be two codes. */
function trimStrings<T>(row: T): T {
  if (!row || typeof row !== "object") return row;
  return Object.fromEntries(
    Object.entries(row as Record<string, unknown>).map(([key, value]) => [key, typeof value === "string" ? value.trim() : value]),
  ) as T;
}
